import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Input from "../../../../componentes/input/input";
import InputFormat from "../../../../componentes/inputMask/inputMask";
import Button from "../../../../componentes/button/button";
import GdmDadosCampos from "../../Shared/GdmDadosCampos";
import RenovacaoGdmService from "../../../../services/resource/renovacaoGdmService";
import SolicitacaoService from "../../../../services/resource/solicitacaoService";
import { tiposAnexoFaltando } from "../../../../utils/solicitacoes/anexosStaged";
import { DOCUMENTOS_OBRIGATORIOS_RENOVACAO_GDM } from "./NovaSolicitacaoRenovacaoGdmForm";

const service = new RenovacaoGdmService();
const solicitacaoService = new SolicitacaoService();

const CODIGO_CLIENTE_REGEX = /^\d{4}-\d{4}$/;
const CONTRATO = "Contrato";

// Mesma GDM física: pelo nº de série; sem série, pelo GDM Ativo.
const mesmaGdm = (a, b) => !!a && !!b && (a.numeroSerie ? a.numeroSerie === b.numeroSerie : !!a.gdmAtivo && a.gdmAtivo === b.gdmAtivo);

/**
 * Etapa de Solicitação da Renovação de GDM numa solicitação já criada (ex.: chegou do app sem anexos, ou foi
 * devolvida): o solicitante edita cliente, GDM e documentos não enviados, confere os documentos obrigatórios
 * contra os anexos da página (o painel "Anexos" ao lado inclui e remove) e envia. "Salvar e enviar" grava os
 * dados (PUT) e executa a ação ENVIAR — o backend não valida anexos, a cobrança é aqui, como na criação.
 */
export default function EtapaRenovacaoGdmSolicitacao({ solicitacao, detalheProcesso, anexos = [], acaoEmExecucao, encaminhar, onAcaoConcluida }) {
    const [iniciado, setIniciado] = useState(false);
    const [clienteCodigo, setClienteCodigo] = useState("");
    const [consulta, setConsulta] = useState(null);
    const [consultando, setConsultando] = useState(false);
    const [baseGdmId, setBaseGdmId] = useState(null);
    const [naoEnviados, setNaoEnviados] = useState({});
    const [enviando, setEnviando] = useState(false);

    const cliente = consulta?.cliente;
    const gdms = consulta?.gdms || [];
    const documentosExigidos = DOCUMENTOS_OBRIGATORIOS_RENOVACAO_GDM.filter(doc => !(doc in naoEnviados));
    const faltando = tiposAnexoFaltando(documentosExigidos, anexos);

    // `detalheProcesso` chega depois do primeiro render — preenche uma vez, quando chegar.
    useEffect(() => {
        if (!detalheProcesso || iniciado) return;
        setIniciado(true);
        setNaoEnviados(Object.fromEntries((detalheProcesso.documentosNaoEnviados || []).map(d => [d.documento, d.motivo || ""])));
        aoMudarCodigoCliente(detalheProcesso.clienteCodigo || "", detalheProcesso.gdm);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detalheProcesso, iniciado]);

    function aoMudarCodigoCliente(valor, gdmAtual = null) {
        const codigo = valor || "";
        setClienteCodigo(codigo);
        setConsulta(null);
        setBaseGdmId(null);
        if (!CODIGO_CLIENTE_REGEX.test(codigo)) return;

        setConsultando(true);
        service.consultarCliente(codigo)
            .then(res => {
                setConsulta(res.data);
                if (!res.data.cliente?.encontrado) toast.warn("Cliente não encontrado no cadastro.");
                const lista = res.data.gdms || [];
                const daBase = lista.find(item => mesmaGdm(item.gdm, gdmAtual));
                if (daBase) setBaseGdmId(daBase.id);
                else if (lista.length === 1) setBaseGdmId(lista[0].id);
            })
            .catch(() => toast.error("Erro ao consultar o cliente."))
            .finally(() => setConsultando(false));
    }

    function alternarNaoEnviado(documento) {
        setNaoEnviados(prev => {
            const proximo = { ...prev };
            if (documento in proximo) delete proximo[documento];
            else proximo[documento] = "";
            return proximo;
        });
    }

    function validar() {
        if (!CODIGO_CLIENTE_REGEX.test(clienteCodigo)) return "Informe o código do cliente no formato 9999-9999.";
        if (!cliente?.encontrado) return "Cliente não encontrado no cadastro — confira o código.";
        if (gdms.length > 0 && !baseGdmId) return "Selecione a GDM que será renovada.";
        const semMotivo = Object.entries(naoEnviados).filter(([, motivo]) => !motivo.trim()).map(([doc]) => doc);
        if (semMotivo.length > 0) return `Informe o motivo de não enviar: ${semMotivo.join(", ")}.`;
        if (faltando.length > 0) {
            return `Anexe no painel "Anexos": ${faltando.join(", ")}. Se não for possível, marque como não enviado e informe o motivo.`;
        }
        return null;
    }

    async function salvarEEnviar() {
        const erro = validar();
        if (erro) {
            toast.error(erro);
            return;
        }
        let usuarioEscolhidoId;
        try {
            usuarioEscolhidoId = await encaminhar("ENVIAR");
        } catch {
            return;
        }
        setEnviando(true);
        try {
            await service.editarSolicitacao(solicitacao.id, {
                clienteCodigo,
                baseGdmId,
                documentosNaoEnviados: Object.entries(naoEnviados).map(([documento, motivo]) => ({ documento, motivo: motivo.trim() })),
                usuarioEscolhidoId,
            });
            const res = await solicitacaoService.executarAcao(solicitacao.id, { acao: "ENVIAR", usuarioEscolhidoId, dados: {} });
            onAcaoConcluida("ENVIAR", res.data);
        } catch (e) {
            toast.error(e?.response?.data?.message || "Não foi possível salvar e enviar a solicitação.");
        } finally {
            setEnviando(false);
        }
    }

    if (!detalheProcesso) {
        return (
            <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                <i className="fa fa-spinner fa-spin mr-2" /> Carregando dados da solicitação...
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <p className="text-sm text-muted-foreground">
                Esta renovação está na etapa inicial e ainda não foi enviada para a aprovação. Revise os dados, confira os
                documentos obrigatórios (anexe pelo painel <strong>Anexos</strong>) e envie.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
                <InputFormat label="Código do Cliente" required mask="9999-9999" value={clienteCodigo}
                    onChange={valor => aoMudarCodigoCliente(valor)} placeholder="0000-0000" />
                <div className="sm:col-span-2">
                    <Input label="Razão Social" disabled value={cliente?.razaoSocial || ""} onChange={() => { }} />
                </div>
                <div className="sm:col-span-2">
                    <Input label="Nome Fantasia" disabled value={cliente?.fantasia || ""} onChange={() => { }} />
                </div>
            </div>
            {consultando && <p className="text-xs text-muted-foreground -mt-2">Consultando cliente e base de GDM...</p>}
            {consulta && !cliente?.encontrado && (
                <p className="text-xs text-destructive -mt-2">Cliente não encontrado no cadastro.</p>
            )}

            <div className="flex flex-col gap-2 rounded-lg border border-border p-3">
                <h3 className="text-sm font-semibold text-foreground">Documentos obrigatórios</h3>
                {DOCUMENTOS_OBRIGATORIOS_RENOVACAO_GDM.map(doc => {
                    const marcado = doc in naoEnviados;
                    const anexado = tiposAnexoFaltando([doc], anexos).length === 0;
                    return (
                        <div key={doc} className="flex flex-col gap-1.5 border-t border-border pt-2 first-of-type:border-t-0 first-of-type:pt-0">
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                                <span className="font-semibold text-foreground min-w-[190px]">{doc}</span>
                                {anexado ? (
                                    <span className="text-xs font-semibold text-success"><i className="fa fa-check-circle mr-1" />Anexado</span>
                                ) : marcado ? (
                                    <span className="text-xs text-muted-foreground"><i className="fa fa-minus-circle mr-1" />Marcado como não enviado</span>
                                ) : (
                                    <span className="text-xs font-semibold text-destructive"><i className="fa fa-exclamation-circle mr-1" />Falta anexar</span>
                                )}
                                <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer select-none">
                                    <input type="checkbox" checked={marcado} onChange={() => alternarNaoEnviado(doc)} />
                                    {doc === CONTRATO ? "Contrato não enviado" : "Cliente não quis compartilhar"}
                                </label>
                            </div>
                            {marcado && (
                                <Input label="Motivo do não envio" required value={naoEnviados[doc]}
                                    onChange={e => setNaoEnviados(prev => ({ ...prev, [doc]: e.target.value }))} />
                            )}
                        </div>
                    );
                })}
            </div>

            {consulta && cliente?.encontrado && (
                <div className="flex flex-col gap-2">
                    <h3 className="text-sm font-semibold text-foreground">
                        GDM do cliente na base {gdms.length > 0 && <span className="text-muted-foreground font-normal">({gdms.length})</span>}
                    </h3>
                    {gdms.length === 0 && <p className="text-sm text-muted-foreground">Nenhuma GDM encontrada na base para este cliente.</p>}
                    {gdms.map(item => {
                        const selecionada = baseGdmId === item.id;
                        return (
                            <button key={item.id} type="button" aria-pressed={selecionada} onClick={() => setBaseGdmId(item.id)}
                                className={`text-left rounded-lg border p-3 transition-colors ${selecionada ? "border-primary bg-primary/5 ring-2 ring-primary/20" : "border-border hover:bg-muted"}`}>
                                <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-foreground">
                                    <i className={selecionada ? "fa fa-check-circle text-primary" : "far fa-circle text-muted-foreground"} />
                                    {item.gdm?.gdmDescricao || item.gdm?.gdmCodigo || "GDM"}
                                    {item.gdm?.numeroSerie && <span className="text-muted-foreground font-normal">— Série {item.gdm.numeroSerie}</span>}
                                </div>
                                <GdmDadosCampos gdm={item.gdm} />
                            </button>
                        );
                    })}
                </div>
            )}

            <div className="flex justify-end">
                <Button variant="blue" label="Salvar e enviar" iconLeft="fa fa-paper-plane"
                    loading={enviando || acaoEmExecucao === "ENVIAR"} disabled={enviando || consultando} onClick={salvarEEnviar} />
            </div>
        </div>
    );
}
